/**
 * API client — wraps fetch to the Worker, attaches the Worker JWT from the
 * single auth store (no split-brain token). Every endpoint returns an
 * ApiResponse envelope; apiFetch unwraps `.data` and throws ApiError otherwise.
 *
 * Typed per-domain helpers live in src/api/<domain>.ts (F3) and call apiFetch +
 * unwrapArray/unwrapData. This file is the transport + the unwrap/url/idem utils.
 */
import { useAuthStore } from './store';
import type { ApiResponse, AuthUser, AuthTenant } from '@humanai/shared';

const API_BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:8787').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  /** Skip attaching the stored JWT (used by unauthenticated endpoints). */
  skipAuth?: boolean;
  /** Explicit bearer override — used by cross-app handoff (`#token=…`) before
   *  the store is populated. */
  token?: string;
  /** Extra headers (e.g. an xlsx Content-Type for benchmark import). */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** The auth layer (F4) registers a handler here so a 401 anywhere can drive a
 *  single signOut, instead of every call site reinventing recovery. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** A 402 PRODUCT_NOT_ACTIVE from any gated route means the tenant hasn't
 *  activated Eval Pilot — the auth layer routes to /activate. */
let onProductInactive: (() => void) | null = null;
export function setProductInactiveHandler(fn: (() => void) | null): void {
  onProductInactive = fn;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  const hasBody = opts.body !== undefined;
  if (hasBody && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  if (opts.token) {
    headers.Authorization = `Bearer ${opts.token}`;
  } else if (!opts.skipAuth) {
    const jwt = useAuthStore.getState().jwt;
    if (jwt) headers.Authorization = `Bearer ${jwt}`;
  }
  if (opts.idempotencyKey) headers['X-Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method: opts.method ?? (hasBody ? 'POST' : 'GET'),
      headers,
      body: hasBody ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    throw new ApiError('NETWORK', 'เชื่อมต่อระบบไม่ได้ กรุณาเช็คอินเทอร์เน็ต', 0, e);
  }

  if (res.status === 204) return undefined as T;

  let json: ApiResponse<T>;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError('INTERNAL', 'ระบบตอบกลับไม่ถูกต้อง', res.status);
  }

  if (!json.ok) {
    if (!opts.skipAuth && res.status === 401 && onUnauthorized) onUnauthorized();
    if (res.status === 402 && json.code === 'PRODUCT_NOT_ACTIVE' && onProductInactive) onProductInactive();
    throw new ApiError(json.code, json.error, res.status, json.details);
  }
  return json.data;
}

/**
 * GET /api/v1/me — special-cased because it returns { ok, user, tenant } at the
 * TOP level (not the standard { ok, data } envelope), so apiFetch (which
 * returns .data) can't read it. Pass an explicit token for the #token / login
 * handoff before the store is populated.
 */
export async function fetchMe(token?: string): Promise<{ user: AuthUser; tenant: AuthTenant | null }> {
  const headers: Record<string, string> = {};
  const jwt = token ?? useAuthStore.getState().jwt;
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  let res: Response;
  try {
    res = await fetch(buildUrl('/api/v1/me'), { headers });
  } catch (e) {
    throw new ApiError('NETWORK', 'เชื่อมต่อระบบไม่ได้ กรุณาเช็คอินเทอร์เน็ต', 0, e);
  }
  const json = (await res.json()) as
    | { ok: true; user: AuthUser; tenant: AuthTenant | null }
    | { ok: false; code: string; error: string; details?: unknown };
  if (!json.ok) {
    if (res.status === 402 && json.code === 'PRODUCT_NOT_ACTIVE' && onProductInactive) onProductInactive();
    throw new ApiError(json.code, json.error, res.status, json.details);
  }
  return { user: json.user, tenant: json.tenant };
}

// ── URL + query helpers ───────────────────────────────────────────────────────
export function buildUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

export function getApiBase(): string {
  return API_BASE;
}

/** Build a `?a=1&b=2` query string, dropping null/undefined/'' values. */
export function qs(
  params?: Record<string, string | number | boolean | null | undefined>,
): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, val] of Object.entries(params)) {
    if (val === null || val === undefined || val === '') continue;
    sp.set(k, String(val));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Resolve a Worker-signed relative file URL (e.g. `/file?path=…&sig=…`) into
 *  an absolute URL usable in <img src>. */
export function resolveFileUrl(signed: string | null | undefined): string | null {
  if (!signed) return null;
  if (signed.startsWith('http')) return signed;
  return `${API_BASE}${signed}`;
}

// ── Response unwrap helpers ────────────────────────────────────────────────────
// Worker list endpoints variously return `T[]`, `{ data: T[] }`, or
// `{ data: { items: T[], total } }`. unwrapArray normalises all three to T[]
// so call sites never defensive-cast (ADR 0002 — kills the vanilla pickArr).
export function unwrapArray<T>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data as T[];
    if (Array.isArray(o.items)) return o.items as T[];
    if (o.data && typeof o.data === 'object') {
      const inner = (o.data as Record<string, unknown>).items;
      if (Array.isArray(inner)) return inner as T[];
    }
  }
  return [];
}

/** Identity pass-through for object responses — documents intent at call sites
 *  and gives a single seam if the envelope shape ever needs massaging. */
export function unwrapData<T>(v: T): T {
  return v;
}

// ── Idempotency ────────────────────────────────────────────────────────────────
let _idemCounter = 0;
/** Generate an idempotency key for ONE logical action. Call once per action
 *  (not per network retry) so a TanStack Query retry doesn't double-write. */
export function makeIdemKey(prefix = 'op'): string {
  _idemCounter += 1;
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${_idemCounter}-${typeof performance !== 'undefined' ? Math.floor(performance.now()) : _idemCounter}`;
  return `${prefix}_${rand}`;
}

// ── File upload (multipart via Worker → R2) ────────────────────────────────────
export async function uploadFile(file: Blob, purpose: string, filename = 'upload.jpg'): Promise<string> {
  const jwt = useAuthStore.getState().jwt;
  const form = new FormData();
  form.append('file', file, filename);
  form.append('purpose', purpose);

  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(), 30_000);
  let res: Response;
  try {
    res = await fetch(buildUrl('/upload'), {
      method: 'POST',
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      body: form,
      signal: ctrl.signal,
    });
  } catch (e) {
    clearTimeout(timeoutId);
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new ApiError('UPLOAD_TIMEOUT', 'อัปโหลดไฟล์ค้างนานเกิน 30 วินาที กรุณาเช็คเน็ตและลองใหม่', 0);
    }
    throw e;
  }
  clearTimeout(timeoutId);
  const json = (await res.json()) as ApiResponse<{ path: string; size?: number }>;
  if (!json.ok) throw new ApiError(json.code, json.error, res.status, json.details);
  return json.data.path;
}
